import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, Eye, Search, ShoppingCart } from 'lucide-react';
import api from '../../utils/api';
import { getPageSeo } from '../../utils/pageSeo';
import { cldImg } from '../../utils/cloudinary';

function CategoryProductsSidebar({ categoryName }) {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [catSlug, setCatSlug] = useState('');

  useEffect(() => {
    if (!categoryName) return;
    setLoading(true);
    api.get('/categories').then(({ data }) => {
      const cat = (data.categories || []).find(
        c => c.name.toLowerCase() === categoryName.toLowerCase()
      );
      if (!cat) { setLoading(false); return; }
      setCatSlug(cat.slug);
      return api.get(`/products?category=${cat.id}&limit=12`);
    }).then(res => {
      if (res) {
        setAllProducts(res.data.products || []);
        setProducts(res.data.products || []);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [categoryName]);

  useEffect(() => {
    if (!search.trim()) {
      setProducts(allProducts);
    } else {
      const q = search.toLowerCase();
      setProducts(allProducts.filter(p => p.name.toLowerCase().includes(q)));
    }
  }, [search, allProducts]);

  if (!categoryName) return null;

  const getPrice = (p) => {
    if (!p.variants?.length) return null;
    const prices = p.variants.map(v => parseFloat(v.discount_price) || parseFloat(v.price) || 0).filter(Boolean);
    return prices.length ? Math.min(...prices) : null;
  };

  return (
    <aside className="w-full lg:w-72 shrink-0 space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <h3 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">
          {categoryName} Products
        </h3>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-yellow"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-brand-yellow border-t-transparent rounded-full animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <p className="text-gray-400 text-xs text-center py-4">No products found</p>
        ) : (
          <div className="space-y-3">
            {products.map(p => {
              const thumb = p.images?.[0]?.image_url;
              const price = getPrice(p);
              return (
                <Link
                  key={p.id}
                  to={`/product/${p.slug}`}
                  className="flex items-center gap-3 group hover:bg-gray-50 rounded-xl p-2 -mx-2 transition-colors"
                >
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                    {thumb
                      ? <img src={cldImg(thumb, 160)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      : <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">No img</div>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 line-clamp-2 group-hover:text-brand-yellow transition-colors">{p.name}</p>
                    {price && <p className="text-xs font-bold text-brand-yellow mt-0.5">₹{price.toLocaleString('en-IN')}</p>}
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {products.length > 0 && catSlug && (
          <Link
            to={`/category/${catSlug}`}
            className="mt-4 flex items-center justify-center gap-2 w-full py-2 rounded-xl bg-brand-yellow text-brand-dark text-xs font-bold hover:opacity-90 transition-opacity"
          >
            <ShoppingCart className="w-3.5 h-3.5" /> View All
          </Link>
        )}
      </div>
    </aside>
  );
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blogs/${slug}`)
      .then(({ data }) => setBlog(data.blog))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (blog && !getPageSeo(window.location.pathname)) {
      // Decode any HTML entities (e.g. a stored "&amp;") so meta text reads "&".
      const decode = (s) => { const el = document.createElement('textarea'); el.innerHTML = s || ''; return el.value; };
      document.title = decode(blog.meta_title || blog.title);
      const setMeta = (name, content) => {
        if (!content) return;
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('name', name);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', decode(content));
      };
      setMeta('description', blog.meta_description || blog.excerpt || '');
      setMeta('keywords', blog.meta_keywords || '');
    }
    return () => { document.title = 'TheYellove'; };
  }, [blog]);

  if (loading) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-brand-yellow border-t-transparent rounded-full animate-spin" /></div>;
  if (notFound) return (
    <div className="text-center py-16">
      <p className="text-gray-500 mb-4">Blog post not found</p>
      <Link to="/blog" className="btn-primary px-6 py-3 rounded-xl">Back to Blog</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 pb-24 md:pb-10">
      <Link to="/blog" className="inline-flex items-center gap-2 text-gray-500 hover:text-brand-dark text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Blog
      </Link>

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Main article */}
        <article className="flex-1 min-w-0">
          {blog.featured_image && (
            <div className="rounded-2xl overflow-hidden mb-6 aspect-video">
              <img src={cldImg(blog.featured_image, 1200)} alt={blog.title} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mb-3">
            {blog.category && <span className="text-brand-yellow font-bold uppercase tracking-wide">{blog.category}</span>}
            {blog.author && <span className="flex items-center gap-1"><User className="w-3 h-3" />{blog.author}</span>}
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(blog.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{blog.views} views</span>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-brand-dark mb-4">{blog.title}</h1>

          {blog.excerpt && <p className="text-gray-500 text-base mb-6 border-l-4 border-brand-yellow pl-4 italic">{blog.excerpt}</p>}

          <div
            className="prose max-w-none text-gray-700 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-brand-dark [&_h1]:mt-6 [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-brand-dark [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-3 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_a]:text-brand-yellow [&_a]:underline [&_strong]:font-bold [&_em]:italic [&_blockquote]:border-l-4 [&_blockquote]:border-brand-yellow [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-500 [&_blockquote]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:my-4 [&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2 [&_th]:border [&_th]:border-gray-200 [&_th]:px-3 [&_th]:py-2 [&_th]:bg-gray-50 [&_th]:font-semibold [&_img]:rounded-xl [&_img]:my-4 [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-xl [&_iframe]:my-4"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {blog.tags && (
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-gray-100">
              <Tag className="w-4 h-4 text-gray-400 mt-0.5" />
              {blog.tags.split(',').map(t => t.trim()).filter(Boolean).map(tag => (
                <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">{tag}</span>
              ))}
            </div>
          )}
        </article>

        {/* Sidebar */}
        {blog.category && (
          <div className="lg:sticky lg:top-24 self-start">
            <CategoryProductsSidebar categoryName={blog.category} />
          </div>
        )}
      </div>
    </div>
  );
}
